/**
 * `withComponentRules` — higher-order component that applies
 * user-authored rules to a registered React component at runtime.
 *
 * Usage:
 * ```ts
 * export const OpenCoverageCardConfigurable = withComponentRules(
 *   OpenCoverageCard,
 *   'claimflow.openCoverageCard',
 * );
 * ```
 *
 * The returned wrapped component:
 *
 * 1. Writes the current props into the {@link runtimePropsStore} on every
 *    render, so the admin config page's live preview can show the exact
 *    props the calling page was passing at CMD/Ctrl+click time.
 * 2. Fetches the list of active rules for the given `componentId` via
 *    {@link useComponentRules} (backed by React Query).
 * 3. Reads the page-level variable bag via {@link useRuleContext}.
 * 4. Memoises the evaluator output on `(props, rules, context)` so the
 *    rule evaluation only re-runs when one of the three changes.
 * 5. Returns `null` if a `hide` effect matched — after a same-rule `show`
 *    and the priority order have been resolved by the evaluator — unless the
 *    caller passes the inbound `__forceVisible` control prop, which overrides
 *    only this component-level hide gate (every other rule effect still
 *    applies to the revealed component).
 * 6. Wraps the output in a `<div data-component-id="...">` container so
 *    the Phase 4 CMD/Ctrl+click handler can locate the component from
 *    any descendant DOM node.
 *
 * Render gate (anti-flash):
 * - While the rule fetch is still in flight — active rules loading
 *   (`isLoading === true`) **or** the tenant-wide inactive-rules query
 *   not yet ready (`!inactiveReady`) — the HOC renders a loading fallback
 *   (a base `Skeleton` by default) **instead of** the wrapped component.
 *   This eliminates the flash of unconfigured content: no brief render of
 *   the un-renamed / un-reordered / would-be-hidden component that then
 *   snaps to its configured state once the async fetch resolves.
 * - The fallback is overridable via the optional `options.loadingFallback`
 *   so a component can supply a better-sized skeleton (or `null` to opt
 *   out of the placeholder entirely).
 *
 * Fail-open (error path is automatic):
 * - On a fetch error React Query sets `isLoading === false` (status
 *   `error`) and both rule hooks resolve to empty lists, so the gate
 *   falls through and the component renders with its original props — no
 *   hang on a skeleton, no infinite placeholder.
 * - A warm cache (rules already present) makes `isLoading` false on the
 *   very first render, so the component reveals already-configured with no
 *   skeleton frame.
 */

import { useEffect, useMemo, type ComponentType, type ReactNode } from 'react';

import { Skeleton } from '@components/base/feedback';

import { ConfigureButton } from './ConfigureButton';
import { ToggleRulesButton } from './ToggleRulesButton';
import {
  useInactiveRuleIdsFor,
  useInactiveRulesReady,
} from './inactiveRulesContext';
import type { LayoutOverrides } from './layoutOverrides';
import { applyRules } from './ruleEvaluator';
import { useRuleContext } from './ruleContext';
import { setRuntimeContext } from './runtimeContextStore';
import { setRuntimeProps } from './runtimePropsStore';
import { useComponentRules } from './useComponentRules';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Optional runtime meta props injected by the HOC alongside the
 * component's own props.
 *
 * - `disabled` — set when a `disable` effect fired. Many base components
 *   already accept a `disabled` boolean, so we forward the same key
 *   rather than inventing a new one. Components that do not consume it
 *   will simply ignore it.
 * - `_configurableRequiredProps` — populated when any `requireProp`
 *   effect fired. Reserved for future introspection use — the HOC only
 *   forwards it when non-empty so opting out is zero-cost.
 * - `_layoutOverrides` — populated when any layout / tooltip effect
 *   (`renameField`, `hideField`, `reorderFields`, the column / section
 *   equivalents, or a tooltip setter) fired. A migrated component merges it
 *   into its declarative config via `applyLayoutOverrides(...)` before
 *   render; components that do not consume it simply ignore the prop. The
 *   HOC only forwards it when at least one such effect fired, so an
 *   un-migrated component (and the no-rules case) sees no change.
 */
interface RuleMetaProps {
  disabled?: boolean;
  _configurableRequiredProps?: string[];
  _layoutOverrides?: LayoutOverrides;
}

/**
 * INBOUND control props a caller may pass to the wrapped component to
 * steer the HOC itself — distinct from {@link RuleMetaProps}, which the
 * HOC *injects* into the inner component.
 *
 * - `__forceVisible` — when `true`, overrides ONLY the component-level
 *   `hide` gate so a rule-hidden component still renders. Every other rule
 *   effect (setProp, disable, layout overrides, …) continues to apply. The
 *   HOC strips this control prop before it reaches the wrapped component,
 *   so no card ever receives it as a real prop. Ephemeral: it lives only
 *   for the render that passes it (e.g. the "Autres actions" launcher
 *   injecting a hidden backing card so it can be scrolled to).
 */
export interface ConfigurableControlProps {
  __forceVisible?: boolean;
}

/**
 * Optional behaviour overrides for {@link withComponentRules}.
 */
export interface WithComponentRulesOptions {
  /**
   * Element rendered while the rule fetch is still in flight, in place of
   * the wrapped component. Omit the key to use the default base
   * `Skeleton`; pass a better-sized skeleton for a closer placeholder; or
   * pass an explicit `null` to render nothing during the gate (opting out
   * of any placeholder). Note: an explicit `null` is honoured — only an
   * absent key falls back to the default.
   */
  loadingFallback?: ReactNode;
}

/**
 * Default loading placeholder used by the render gate when a caller does
 * not supply `options.loadingFallback`. A single rectangular skeleton —
 * intentionally simple; components that want a closer fit pass their own.
 */
const DEFAULT_LOADING_FALLBACK: ReactNode = (
  <Skeleton variant="rectangular" height={120} />
);

// ---------------------------------------------------------------------------
// HOC
// ---------------------------------------------------------------------------

/**
 * Wrap a React component with runtime rule evaluation.
 *
 * The `Component` argument keeps its props type exactly as declared; the
 * HOC itself does not require any additional props on the caller side.
 * Rule-authored mutations flow through the props object via `setProp` /
 * `filterPropValues` effects and are only visible to the wrapped
 * component.
 *
 * @param Component   - The raw (unwrapped) React component.
 * @param componentId - The registration id used by the registry, the
 *                      click handler, and the rule service. Must match
 *                      the id passed to `registerComponent()` for the
 *                      same component class.
 * @param options     - Optional behaviour overrides — currently a custom
 *                      `loadingFallback` for the render gate.
 * @returns A new React component that renders `Component` with rule
 *          effects applied. `displayName` is prefixed with
 *          `withComponentRules(...)` for DevTools readability.
 */
export function withComponentRules<P extends Record<string, unknown>>(
  Component: ComponentType<P>,
  componentId: string,
  options?: WithComponentRulesOptions,
): ComponentType<P & ConfigurableControlProps> {
  // Distinguish "not supplied" (use the default skeleton) from an explicit
  // `null` (opt out of any placeholder). `??` would conflate the two, so we
  // test for the key's presence instead.
  const loadingFallback: ReactNode =
    options !== undefined && 'loadingFallback' in options
      ? options.loadingFallback
      : DEFAULT_LOADING_FALLBACK;

  function Wrapped(props: P): ReactNode {
    // Inbound control prop, read by field access rather than destructuring
    // `props` into a new object — a fresh object identity every render would
    // defeat the `useMemo`/`useEffect` deps below (used by every configurable
    // component). Stripped from the inner props before render (see below).
    const forceVisible =
      (props as { __forceVisible?: boolean }).__forceVisible === true;

    const { rules, isLoading } = useComponentRules(componentId);
    const context = useRuleContext();
    const inactiveIds = useInactiveRuleIdsFor(componentId);
    const inactiveReady = useInactiveRulesReady();

    // Runtime props capture for the live preview. We write on every
    // render so the last-seen render wins; the `useEffect` with `props`
    // dep ensures the write happens after render and observes the same
    // props object the component rendered with.
    useEffect(() => {
      setRuntimeProps(componentId, props);
    }, [props]);

    // Runtime context capture for the admin rule-authoring page. We
    // skip empty bags because `useRuleContext()` returns `{}` outside any
    // provider — writing it would overwrite a real capture from another
    // component on the same page. The guard keeps the last real capture.
    useEffect(() => {
      if (
        context !== null &&
        typeof context === 'object' &&
        Object.keys(context).length > 0
      ) {
        const ctxRecord = context as Record<string, unknown>;
        const claimRefRaw = ctxRecord.claim_reference;
        const claimReference =
          typeof claimRefRaw === 'string' && claimRefRaw !== ''
            ? claimRefRaw
            : undefined;
        setRuntimeContext(componentId, ctxRecord, {
          capturedAt: new Date().toISOString(),
          claimReference,
        });
      }
    }, [context]);

    // Filter out rules the tenant has locally suppressed via the
    // inactive-rules map. When the set is empty this short-circuits to
    // the original list so behaviour is identical to the pre-feature
    // runtime.
    const activeRules = useMemo(() => {
      if (inactiveIds.size === 0) return rules;
      return rules.filter((rule) => !inactiveIds.has(rule.id));
    }, [rules, inactiveIds]);

    // Evaluate rules lazily. The render gate below guarantees we never
    // reach the `applyRules` output while a fetch is still in flight, so
    // this memo always runs against settled rule lists.
    const evaluated = useMemo(
      () => applyRules(props, activeRules, context),
      [props, activeRules, context],
    );

    // Render gate (anti-flash). While either the active-rules fetch is in
    // flight or the tenant-wide inactive-rules query is not yet ready, we
    // render the loading fallback INSTEAD of the wrapped component, so the
    // un-configured component never flashes before its rules apply. This
    // branch sits AFTER every hook call so hook order stays stable across
    // renders. On a fetch error React Query reports `isLoading === false`
    // and the hooks resolve to empty lists, so `notReady` becomes false
    // and the component renders fail-open with its original props.
    const notReady = isLoading || !inactiveReady;
    if (notReady) {
      return loadingFallback;
    }

    // The `__forceVisible` control prop overrides ONLY this hide gate, so a
    // rule-hidden component can still be revealed on demand (e.g. the "Autres
    // actions" launcher injecting a hidden backing card). All other evaluated
    // effects below still apply.
    if (evaluated.hidden && !forceVisible) {
      return null;
    }

    const metaProps: RuleMetaProps = {};
    if (evaluated.disabled) {
      metaProps.disabled = true;
    }
    if (evaluated.requiredProps.length > 0) {
      metaProps._configurableRequiredProps = evaluated.requiredProps;
    }
    if (evaluated.layoutOverrides !== undefined) {
      metaProps._layoutOverrides = evaluated.layoutOverrides;
    }

    // Type gymnastics: React's HOC typing does not easily express "the
    // wrapped component receives P plus optional meta props the caller
    // never supplied". A cast to `P` is safe because:
    // - `disabled` is optional in `RuleMetaProps` and only set when the
    //   evaluator fired a `disable` effect (the component chooses
    //   whether to use it);
    // - `_configurableRequiredProps` and `_layoutOverrides` are dedicated
    //   sentinel names that no base component uses today; a migrated
    //   component opts in by reading `_layoutOverrides`, all others ignore it.
    const combinedProps = { ...evaluated.props, ...metaProps } as P;
    // Strip the inbound control prop so the wrapped component never receives
    // `__forceVisible` as a real prop (it steers the HOC only).
    delete (combinedProps as Record<string, unknown>).__forceVisible;

    return (
      <div
        data-component-id={componentId}
        className="group"
        style={{ position: 'relative' }}
      >
        <div className="absolute top-1 right-1 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <ConfigureButton componentId={componentId} />
          <ToggleRulesButton componentId={componentId} />
        </div>
        <Component {...combinedProps} />
      </div>
    );
  }

  const wrappedName =
    Component.displayName ??
    (Component.name !== '' ? Component.name : 'Component');
  Wrapped.displayName = `withComponentRules(${wrappedName})`;

  return Wrapped;
}
